import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CreateWorkflowRequest, QueryKey, Workflow, WorkflowSummary } from 'api/model.ts';
import {
  batchDeleteWorkflows,
  createWorkflow,
  getWorkflows,
  getWorkflowsSummary,
  updateWorkflow,
} from 'api/workflows/requests';
import { toast } from 'react-toastify';
import { useParams } from 'react-router';
import { useCallback } from 'react';
import { errorHandler } from 'api/axios_instance';

const queryKey = QueryKey.Workflows;

type Options = {
  projectId?: string;
  summary?: boolean;
  enabled?: boolean;
};

const useWorkflows = (options?: Options) => {
  const { projectId: projectIdParam } = useParams();
  const projectId = options?.projectId ?? projectIdParam ?? '';

  const queryClient = useQueryClient();

  const { data: workflows, isLoading } = useQuery<WorkflowSummary[] | Workflow[]>({
    queryKey: [queryKey, { projectId }],
    queryFn: () => (options?.summary ? getWorkflowsSummary(projectId) : getWorkflows(projectId)),
    enabled: !!projectId && (options?.enabled ?? true),
    staleTime: 1000 * 60 * 5,
  });

  const create = useMutation({
    mutationFn: (workflow: CreateWorkflowRequest) => createWorkflow(projectId, workflow),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success('Workflow successfully created');
    },
    onError: errorHandler('Creating workflow failed'),
  });

  const updateSilent = useMutation({
    mutationFn: (workflow: CreateWorkflowRequest) => updateWorkflow(projectId, workflow.id!, workflow),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  const batchDelete = useMutation({
    mutationFn: (workflowIds: string[]) => batchDeleteWorkflows({ workflow_ids: workflowIds }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
    onError: errorHandler('Batch workflow delete failed'),
  });

  const clearQueryKey = () => {
    void queryClient.invalidateQueries({ queryKey: [queryKey] });
  };

  const getWorkflowById = useCallback(
    () => (id: string) => workflows?.find((workflow) => workflow.id === id),
    [workflows],
  );

  const getNodeById = useCallback(
    (nodeId: string) =>
      (workflows as Workflow[])
        ?.map((workflow) => workflow.nodes)
        .flat()
        .find((node) => node.id === nodeId),
    [workflows],
  );

  return {
    workflows,
    isLoading,
    create,
    updateSilent,
    batchDelete,
    clearQueryKey,
    getWorkflowById,
    getNodeById,
  };
};

export { queryKey };
export default useWorkflows;
